import {
  Col,
  Divider,
  Flex,
  Image,
  Row,
  Typography,
} from "antd";
import { HD_VIDEO_CONSTRAINTS, VideoConstraintsResolution } from "./type";
import { useEffect, useRef } from "react";
import MWebcam from "react-webcam";
import { useWebcam } from "./hooks/use-webcam";
import { useTranslation } from "react-i18next";
import { useDelayFocus } from "../../../../hooks/use-delay-focus";
import sampleImage from "../../../../assets/sample-photo-capture.png";
import "./styles.less";
import PprButton from "../../../shared/ppr-button/ppr-button";
import PprChipStatus from "../../../shared/ppr-chip-status/ppr-chip-status";
import PprDropdownSelect from "../../../shared/ppr-dropdown-select/ppr-dropdown-select";

const { Title, Text } = Typography;

export type WebcamOptions = {
  videoConstraints: VideoConstraintsResolution;
};

const DEFAULT_WEBCAM_OPTIONS: WebcamOptions = {
  videoConstraints: HD_VIDEO_CONSTRAINTS,
};

const DELAY_FOCUS_NEXT_BUTTON = 5000;

interface WebcamProps {
  webcamCheck: boolean;
  options?: WebcamOptions;
  onComplete: (delayFocus?: number) => void;
  onFail: (
    errorList: string[],
    title?: string,
    showFAQLink?: boolean,
    canRetry?: boolean,
  ) => void;
}

const WEBCAM_PREFIX = "SYSTEM_CHECK_STEPPER.WEBCAM";

const VideoCheck = ({
  webcamCheck,
  onComplete,
  onFail,
  options = DEFAULT_WEBCAM_OPTIONS,
}: WebcamProps) => {
  const webcamRef = useRef<MWebcam>(null);
  const {
    selectedCamera,
    setSelectedCamera,
    cameras,
    cameraStatus,
    handleCheckCamera,
    inCheckingTime,
    permissionState,
    errorMessage,
    removeIntervalCheck,
  } = useWebcam({ webcamRef });
  const { t } = useTranslation("translation", { keyPrefix: WEBCAM_PREFIX });
  const selectRef = useRef<any>(null);
  useDelayFocus(
    selectRef,
    cameraStatus !== "failed" && permissionState === "granted",
    28000,
  );

  const renderStatusTitle = () => {
    const prefix = "WEBCAMANALYSISSTATUS.";
    if (cameraStatus === "failed" || cameraStatus === "not-started")
      return t(prefix + "NOT-STARTED");

    if (cameraStatus === "passed") return t(prefix + "COMPLETED");

    if (cameraStatus === "analyzing") return t(prefix + "ANALYZING");

    return "";
  };

  useEffect(() => {
    if (cameraStatus === "analyzing" || cameraStatus === "not-started") {
      onFail([]);
    }

    if (cameraStatus === "passed") {
      onComplete(DELAY_FOCUS_NEXT_BUTTON);
    } else {
      const errors = [];
      let showFAQLink = true;
      if (cameraStatus === "failed") {
        if (errorMessage?.length > 0) {
          errors.push(...errorMessage);
          showFAQLink = false;
        } else {
          errors.push(`${WEBCAM_PREFIX}.ERRORALERT`);
        }
      }

      onFail(errors, "", showFAQLink);
    }
  }, [cameraStatus, permissionState, errorMessage]);

  return (
    webcamCheck && (
      <Flex vertical className="video-check-container">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Title data-cy="webcam-check-title" tabIndex={0} level={2}>
              {t(`CAMSELECTION`)}
            </Title>
            <Flex vertical gap={16}>
              <Text tabIndex={0}>{t(`DESCRIPTION`)}</Text>
              <Text tabIndex={0}>{t(`DESCRIPTIONTWO`)}</Text>
              <Image
                alt="sample-photo-capture"
                src={sampleImage}
                width={"70%"}
                preview={false}
              />
              <ol className="webcam-detail">
                <li>
                  <Text>1.</Text>
                  <Text className="guide-detail">{t(`INSTRUCTIONS.0`)}</Text>
                </li>
                <li>
                  <Text>2.</Text>
                  <Text className="guide-detail">{t(`INSTRUCTIONS.1`)}</Text>
                </li>
                <li>
                  <Text>3.</Text>
                  <Text className="guide-detail">{t(`INSTRUCTIONS.2`)}</Text>
                </li>
              </ol>
            </Flex>
          </Col>
          <Col span={12}>
            <Flex vertical gap={16}>
              <PprDropdownSelect
                title={t(`LISTDESCRIPTION`)}
                disabled={inCheckingTime}
                selectRef={selectRef}
                options={cameras.map((e) => ({
                  value: e.deviceId,
                  label: e.label,
                }))}
                value={selectedCamera?.deviceId ?? null}
                onChange={(e) => {
                  setSelectedCamera(cameras.find((c) => c.deviceId === e));
                }}
                className="custom-select"
                placeholder="-"
              />
              <Flex vertical>
                <Title level={4}>{t(`TITLECAMERAPREVIEW`)}</Title>
                <MWebcam
                  ref={webcamRef}
                  audio={false}
                  width="100%"
                  height="100%"
                  videoConstraints={{
                    ...options.videoConstraints,
                    deviceId: { exact: selectedCamera?.deviceId },
                  }}
                  screenshotFormat="image/jpeg"
                  className={"webcam-preview"}
                />

                <PprButton
                  data-cy="webcam-button"
                  onClick={handleCheckCamera}
                  disable={
                    inCheckingTime ||
                    !selectedCamera ||
                    cameras.length === 0 ||
                    permissionState === "denied" ||
                    cameraStatus === "passed"
                  }
                  className="camera-check-button"
                  text={t(`CHECKBUTTON`)}
                />
              </Flex>
            </Flex>
          </Col>
        </Row>
        <Divider className="custom-divider" />
        <Flex align="flex-start" justify="space-between">
          <Flex vertical flex={1}>
            <Title level={3}>{t(`WEBCAMANALYSISTITLE`)}</Title>
            <Text>{renderStatusTitle()}</Text>
          </Flex>
          <PprChipStatus status={cameraStatus} />
        </Flex>
      </Flex>
    )
  );
};

export default VideoCheck;
